import { AUCTION_MAX_LISTINGS, type AuctionListing, getItem } from '@shefira/shared';

import { cancelListing, collectListing, myListings } from '../net/auction.js';
import { type AuctionDesk, failureText, lotRow, priceTag, timeLeft } from './auction-format.js';
import { hideItemHover } from './item-hover.js';
import { uiFormat, uiText } from './text.js';

/**
 * « Mes lots » (ADR 0035): what I put up at this house, each a small card.
 * An active lot can be withdrawn; an expired one, or one withdrawn while the
 * bag was full, waits to be collected. A sold lot is gone: its gold reached
 * the purse at the sale.
 */

export interface AuctionMineOptions {
  /** My lots changed: the tab's count, the sell side's limit, the buy side's « Ton lot ». */
  onChanged: () => void;
}

const STATUS_LABELS: Record<AuctionListing['status'], string> = {
  active: 'En vente',
  sold: 'Vendu',
  expired: 'Expiré',
  cancelled: 'Retiré',
  collected: 'Récupéré',
  revoked: 'Repris (remboursé)',
};

export class AuctionMineView {
  readonly root: HTMLElement;
  private readonly count: HTMLElement;
  private readonly list: HTMLElement;
  private mine: AuctionListing[] = [];
  private busy = false;

  constructor(
    private readonly desk: AuctionDesk,
    private readonly options: AuctionMineOptions,
  ) {
    this.root = document.createElement('div');
    this.root.className = 'auction__view auction__view--mine';
    this.count = document.createElement('p');
    this.count.className = 'bench__hint auction__count';
    this.list = document.createElement('div');
    this.list.className = 'bench__list auction__list auction__list--mine';
    this.root.append(this.count, this.list);
    this.render();
  }

  get lots(): readonly AuctionListing[] {
    return this.mine;
  }

  get activeCount(): number {
    return this.mine.filter((listing) => listing.status === 'active').length;
  }

  /** Whether an id is one of my lots, whatever its state. */
  owns(listingId: string): boolean {
    return this.mine.some((listing) => listing.id === listingId);
  }

  refresh(): void {
    void this.desk
      .access()
      .then((ticket) => myListings(this.desk.token, ticket))
      .then((listings) => {
        this.mine = listings;
        this.render();
        this.options.onChanged();
      })
      .catch((error: unknown) => {
        this.desk.say(uiText(failureText(error)), 'refused');
      });
  }

  private render(): void {
    hideItemHover();
    this.count.textContent = uiFormat('Lots en vente : {active}/{max}', {
      active: this.activeCount,
      max: AUCTION_MAX_LISTINGS,
    });
    this.list.replaceChildren();
    if (this.mine.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'bench__hint auction__empty';
      empty.textContent = uiText('Rien en vente pour l’instant.');
      this.list.append(empty);
      return;
    }
    // What waits to be collected first: it is what one comes here to do.
    const waiting = this.mine.filter((listing) => listing.status !== 'active');
    const active = this.mine.filter((listing) => listing.status === 'active');
    for (const listing of [...waiting, ...active]) this.list.append(this.row(listing));
  }

  private row(listing: AuctionListing): HTMLElement {
    const { row, meta, actions } = lotRow(listing, this.desk.icons, this.desk.wearer);
    row.dataset.status = listing.status;
    const isActive = listing.status === 'active';
    meta.textContent = isActive
      ? uiFormat('Se termine dans {time}', { time: timeLeft(listing.expiresAt) })
      : uiFormat('{status} · à récupérer', { status: uiText(STATUS_LABELS[listing.status]) });
    if (!isActive) meta.classList.add('auction__meta--collect');
    const action = document.createElement('button');
    action.type = 'button';
    action.className = isActive ? 'smith__go auction__withdraw' : 'smith__go auction__collect';
    action.textContent = uiText(isActive ? 'Retirer' : 'Récupérer');
    action.disabled = this.busy;
    action.addEventListener('click', () => {
      this.act(listing, isActive ? cancelListing : collectListing);
    });
    actions.append(priceTag(listing.price), action);
    return row;
  }

  private act(
    listing: AuctionListing,
    request: (token: string, ticket: string, id: string) => Promise<AuctionListing>,
  ): void {
    this.busy = true;
    this.render();
    void this.desk
      .access()
      .then((ticket) => request(this.desk.token, ticket, listing.id))
      .then((result) => {
        const name = uiText(getItem(result.templateId)?.name ?? result.templateId);
        // A withdrawn lot only stays at the house when the bag could not take it back.
        this.desk.say(
          result.status === 'cancelled'
            ? uiFormat('{name} t’attend à l’hôtel des ventes : fais de la place dans ton sac.', {
                name,
              })
            : uiFormat('{name} revient dans ton sac.', { name }),
          result.status === 'cancelled' ? 'refused' : 'success',
        );
      })
      .catch((error: unknown) => {
        this.desk.say(uiText(failureText(error)), 'refused');
      })
      .finally(() => {
        this.busy = false;
        this.refresh();
      });
  }
}
